import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class SltDelivery extends Component {
  #controller;
  #revision = 0;
  #placementObserver;
  #pending = false;
  #cartChanged = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const details = this.closest('.product-details');
    if (details) {
      this.#placementObserver = new MutationObserver(() => this.#syncAfterpay());
      this.#placementObserver.observe(details, { childList: true, subtree: true });
    }
    this.#syncAfterpay();
    this.addEventListener('click', this.#onDisclosure, { signal: this.#controller.signal });
    this.closest('.shopify-section')?.addEventListener('change', this.#refreshEligibility, { signal: this.#controller.signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart, { signal: this.#controller.signal });
    this.#refreshEligibility();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#placementObserver?.disconnect();
    this.#revision += 1;
    super.disconnectedCallback();
  }

  #onDisclosure = (event) => {
    const dialog = this.querySelector('dialog');
    if (event.target.closest('[data-delivery-open]')) dialog?.showModal();
    if (event.target.closest('[data-delivery-close]')) dialog?.close();
  };

  #onCart = () => {
    this.#cartChanged = true;
    this.#refreshEligibility();
  };

  #refreshEligibility = () => {
    const form = this.closest('.product-details')?.querySelector('product-form-component');
    const quantity = form?.querySelector('[name="quantity"]')?.value ?? this.dataset.initialQuantity;
    const hasDiscountHint = new URL(location.href).searchParams.has('discount');
    const eligible = this.dataset.estimateEligible === 'true' && Number(quantity) === 1 && !this.#pending && !this.#cartChanged && !hasDiscountHint;
    const estimate = this.querySelector('[data-delivery-estimate]');
    const fallback = this.querySelector('[data-delivery-fallback]');
    if (estimate) estimate.hidden = !eligible;
    if (fallback) fallback.hidden = eligible;
  };

  #syncAfterpay() {
    const price = Number(this.dataset.variantPrice);
    const amount = (price / 100).toFixed(2);
    const placements = this.closest('.product-details')?.querySelectorAll('afterpay-placement[data-amount][data-currency], square-placement[data-amount][data-currency]') ?? [];
    for (const placement of placements) {
      if (placement.getAttribute('data-currency') !== this.dataset.currency) continue;
      const unavailable = this.#pending || !this.dataset.variantPrice || !Number.isFinite(price);
      placement.toggleAttribute('data-slt-price-unavailable', unavailable);
      if (unavailable) continue;
      if (placement.getAttribute('data-amount') !== amount) placement.setAttribute('data-amount', amount);
    }
  }

  #onSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const revision = ++this.#revision;
    this.#pending = true;
    this.#syncAfterpay();
    this.#refreshEligibility();
    const status = this.querySelector('[data-delivery-status]');
    status?.setAttribute('aria-busy', 'true');
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || revision !== this.#revision) return;
      if (String(detail?.productId) !== this.dataset.productId) return;
      const replacement = [...(detail.html?.querySelectorAll('slt-delivery') ?? [])]
        .find(node => node.dataset.blockId === this.dataset.blockId && node.dataset.productId === this.dataset.productId);
      const nextStatus = replacement?.querySelector('[data-delivery-status]');
      if (status && nextStatus) {
        this.#pending = false;
        this.dataset.variantPrice = replacement.dataset.variantPrice;
        this.dataset.currency = replacement.dataset.currency;
        this.dataset.estimateEligible = replacement.dataset.estimateEligible;
        this.dataset.initialQuantity = replacement.dataset.initialQuantity;
        this.#syncAfterpay();
        status.replaceChildren(...[...nextStatus.childNodes].map(node => node.cloneNode(true)));
        this.#refreshEligibility();
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[slt-delivery] Variant estimate unavailable', error);
    } finally {
      if (revision === this.#revision) status?.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('slt-delivery')) customElements.define('slt-delivery', SltDelivery);
